import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Badge, Button, Card, Collapse, Form, Input, InputNumber, Modal, Popconfirm, Popover, Segmented, Select,
    Space, Switch, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import PageHeader from '../../../shared/components/PageHeader';
import { useTranslation } from '../../../shared/i18n';

// Admin: BGP announcements (api EvmBgp). Everything defaults to off; live
// needs the node name typed, and the agent only peers overlay/loopback
// neighbors unless the node's conf.ini [bgp] peer_allow says otherwise.

const { Text } = Typography;
const BASE = '/api/admin/evm/bgp';
const call = async (method, url, data) => {
    try {
        const res = method === 'get' ? await apiClient.get(BASE + url, { params: data }) : await apiClient.post(BASE + url, data);
        return res.data || {};
    } catch (e) {
        return { status: 'error', msg: e.response?.data?.msg || e.message };
    }
};
const MODE_COLOR = { off: 'default', dryrun: 'blue', live: 'red' };
const STATE_COLOR = { established: 'green', active: 'orange', connect: 'orange', opensent: 'gold', openconfirm: 'gold', idle: 'default', refused: 'red' };
const SRC_COLOR = { eip: 'purple', node_v6: 'cyan', manual: 'default', byoip: 'magenta' };
const APPROVAL_COLOR = { approved: 'green', pending: 'orange', rejected: 'red', revoked: 'default' };

const dur = (s) => {
    s = Number(s) || 0;
    if (s <= 0) return '-';
    const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600); const m = Math.floor((s % 3600) / 60);
    return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
};
const prefixList = (list) => (list?.length ? <div style={{ maxHeight: 240, overflow: 'auto' }}>{list.map((p) => <div key={p}><Text code>{p}</Text></div>)}</div> : '-');

const NodeModal = ({ node, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const mode = Form.useWatch('mode', form);
    const wasLive = node?.config?.mode === 'live';
    useEffect(() => {
        if (!node) return;
        const c = node.config || {};
        form.setFieldsValue({
            mode: c.mode || 'off', local_asn: c.local_asn, router_id: c.router_id || node.overlay_ip || '',
            import_mode: c.import_mode || 'none', announce_eips: !!c.announce_eips, node_v6_prefix: c.node_v6_prefix || '',
            listen_port: c.listen_port || 179, confirm: '',
        });
    }, [node, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const res = await call('post', `/node/${node.id}`, v);
        setSaving(false);
        if (res.status === 'success') { message.success(t('Saved')); onSaved(); } else message.error(res.msg || t('Failed to save'));
    };
    return (
        <Modal open={!!node} title={`${t('BGP settings')} · ${node?.name || ''}`} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose>
            <Form form={form} layout="vertical">
                <Form.Item name="mode" label={t('Mode')} tooltip={t('Dry run only computes what would be announced; no sessions')}>
                    <Segmented options={[{ value: 'off', label: t('Close') }, { value: 'dryrun', label: t('Dry run') }, { value: 'live', label: t('Live') }]} />
                </Form.Item>
                {mode === 'live' && !wasLive && (
                    <Form.Item name="confirm" label={t('Type the node name to go live')} rules={[{ required: true, validator: (_, v) => (v === node?.name ? Promise.resolve() : Promise.reject(new Error(t('Node name does not match')))) }]}>
                        <Input placeholder={node?.name} autoComplete="off" />
                    </Form.Item>
                )}
                <Space wrap>
                    <Form.Item name="local_asn" label={t('Local ASN')}><InputNumber min={1} max={4294967294} style={{ width: 160 }} /></Form.Item>
                    <Form.Item name="router_id" label="Router ID"><Input style={{ width: 160 }} /></Form.Item>
                </Space>
                <Form.Item name="announce_eips" label={t('Announce EIPs from their node')} valuePropName="checked" tooltip={t('Only /32s inside approved owned prefixes with floating enabled')}><Switch /></Form.Item>
                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <>
                            <Form.Item name="import_mode" label={t('Received routes')} tooltip={t('Never installed into the kernel routing table')}>
                                <Select options={[{ value: 'none', label: t('Don\'t accept') }, { value: 'rib', label: t('Accept into BGP table') }]} />
                            </Form.Item>
                            <Form.Item name="node_v6_prefix" label={t('Node IPv6 prefix')}><Input placeholder="2001:db8:1::/48" /></Form.Item>
                            <Form.Item name="listen_port" label={t('Listening port')}><InputNumber min={1} max={65535} /></Form.Item>
                        </>
                    ),
                }]} />
            </Form>
        </Modal>
    );
};

const NeighborModal = ({ value, onClose, onSaved, externalAllowed }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!value) return;
        form.setFieldsValue({
            address: '', remote_asn: undefined, local_address: '', enabled: false, families: ['ipv4'],
            hold_time: 90, multihop: 0, max_prefixes: 100, graceful_restart: 120, passive: false, description: '', ...value, password: '',
        });
    }, [value, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const res = value.id ? await call('post', `/neighbor/${value.id}`, v) : await call('post', '/neighbor', { ...v, node_id: value.node_id });
        setSaving(false);
        if (res.status === 'success') { message.success(t('Saved')); onSaved(); } else message.error(res.msg || t('Failed to save'));
    };
    return (
        <Modal open={!!value} title={value?.id ? t('Edit neighbor') : t('Add neighbor')} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose>
            {!externalAllowed && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('Only internal interconnect/loopback neighbors are allowed for now')} />}
            <Form form={form} layout="vertical">
                <Space wrap>
                    <Form.Item name="address" label={t('Neighbor address')} rules={[{ required: true }]}><Input style={{ width: 220 }} /></Form.Item>
                    <Form.Item name="remote_asn" label={t('Remote ASN')} rules={[{ required: true }]}><InputNumber min={1} max={4294967294} style={{ width: 160 }} /></Form.Item>
                </Space>
                <Space wrap>
                    <Form.Item name="local_address" label={t('Local address')}><Input style={{ width: 220 }} /></Form.Item>
                    <Form.Item name="password" label={t('MD5 password')} tooltip={value?.has_password ? t('Leave empty to keep the current password') : undefined}><Input.Password style={{ width: 160 }} autoComplete="new-password" /></Form.Item>
                </Space>
                <Form.Item name="enabled" label={t('Enable')} valuePropName="checked"><Switch /></Form.Item>
                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <>
                            <Form.Item name="families" label={t('Address families')}><Select mode="multiple" options={[{ value: 'ipv4', label: 'IPv4' }, { value: 'ipv6', label: 'IPv6' }]} /></Form.Item>
                            <Space wrap>
                                <Form.Item name="hold_time" label={t('Hold time')}><InputNumber min={0} max={65535} addonAfter="s" /></Form.Item>
                                <Form.Item name="multihop" label="eBGP multihop" tooltip={t('0 = directly connected')}><InputNumber min={0} max={255} /></Form.Item>
                                <Form.Item name="max_prefixes" label={t('Max prefixes')}><InputNumber min={1} max={1000000} /></Form.Item>
                                <Form.Item name="graceful_restart" label={t('Graceful restart')} tooltip={t('Seconds the peer keeps our routes while the node restarts; 0 = off')}><InputNumber min={0} max={4095} addonAfter="s" /></Form.Item>
                            </Space>
                            <Form.Item name="passive" label={t('Passive')} valuePropName="checked"><Switch /></Form.Item>
                            <Form.Item name="description" label={t('Notes')}><Input /></Form.Item>
                            {value?.has_password && <Form.Item name="clear_password" label={t('Clear password')} valuePropName="checked"><Switch /></Form.Item>}
                        </>
                    ),
                }]} />
            </Form>
        </Modal>
    );
};

const PrefixModal = ({ value, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (value) form.setFieldsValue({ prefix: '', source: 'manual', desired_state: 'announce', communities: '', med: null, local_pref: null, note: '', ...value });
    }, [value, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const res = value.id ? await call('post', `/prefix/${value.id}`, v) : await call('post', '/prefix', { ...v, node_id: value.node_id });
        setSaving(false);
        if (res.status === 'success') { message.success(t('Saved')); onSaved(); } else message.error(res.msg || t('Failed to save'));
    };
    return (
        <Modal open={!!value} title={value?.id ? t('Edit announced prefix') : t('Add announced prefix')} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose>
            <Form form={form} layout="vertical">
                <Form.Item name="prefix" label={t('Prefix')} rules={[{ required: true }]} tooltip={t('Must be inside an approved owned prefix')}><Input placeholder="192.0.2.0/24" disabled={!!value?.id} /></Form.Item>
                <Space wrap>
                    <Form.Item name="source" label={t('Source')}><Select style={{ width: 140 }} options={['manual', 'node_v6', 'byoip'].map((s) => ({ value: s, label: s }))} /></Form.Item>
                    <Form.Item name="desired_state" label={t('Desired state')}>
                        <Segmented options={[{ value: 'announce', label: t('Announce') }, { value: 'withdraw', label: t('Withdraw') }]} />
                    </Form.Item>
                </Space>
                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <>
                            <Form.Item name="communities" label="Communities"><Input placeholder="65000:100 65000:200" /></Form.Item>
                            <Space wrap>
                                <Form.Item name="med" label="MED"><InputNumber min={0} /></Form.Item>
                                <Form.Item name="local_pref" label="Local Pref"><InputNumber min={0} /></Form.Item>
                            </Space>
                            <Form.Item name="note" label={t('Notes')}><Input /></Form.Item>
                        </>
                    ),
                }]} />
            </Form>
        </Modal>
    );
};

const NodeDetail = ({ node, reload, externalAllowed }) => {
    const { t } = useTranslation();
    const [nb, setNb] = useState(null);
    const [px, setPx] = useState(null);
    const sessions = node.status?.neighbors || [];
    const del = async (url) => {
        const res = await call('post', url);
        res.status === 'success' ? message.success(t('Deleted')) : message.error(res.msg || t('Failed'));
        reload();
    };
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            {node.status?.error && <Alert type="warning" showIcon message={node.status.error} />}
            <Card size="small" title={t('Sessions')} extra={(
                <Popover title="RIB" content={prefixList(node.status?.rib_learned)}>
                    <Button type="link" size="small" style={{ padding: 0 }}>RIB {(node.status?.rib_learned || []).length}</Button>
                </Popover>
            )}>
                <Table size="small" rowKey="address" dataSource={sessions} pagination={false} locale={{ emptyText: node.config?.mode === 'live' ? t('No sessions') : t('No sessions outside live mode') }}
                    columns={[
                        { title: t('Neighbor'), dataIndex: 'address', render: (a) => <Text code>{a}</Text> },
                        { title: 'ASN', dataIndex: 'remote_asn' },
                        {
                            title: t('Status'), dataIndex: 'state', render: (s, r) => (
                                <Space size={4}>
                                    <Tooltip title={r.reason}><Tag color={STATE_COLOR[s]}>{s}</Tag></Tooltip>
                                    {r.peer_restarting && <Tooltip title={t('Peer restarting, keeping its routes')}><Tag color="gold">GR</Tag></Tooltip>}
                                </Space>
                            ),
                        },
                        { title: t('Established'), dataIndex: 'uptime', render: dur },
                        { title: t('Received / accepted'), render: (_, r) => <Popover title={t('Prefixes received')} content={prefixList(r.prefixes_in)}><Button type="link" size="small" style={{ padding: 0 }}>{r.received} / {r.accepted}</Button></Popover> },
                        { title: t('Sent'), render: (_, r) => <Popover title={t('Prefixes sent')} content={prefixList(r.prefixes_out)}><Button type="link" size="small" style={{ padding: 0 }}>{r.advertised}</Button></Popover> },
                        { title: t('Flaps'), dataIndex: 'flops' },
                    ]} />
            </Card>
            <Card size="small" title={t('Neighbors')} extra={<Button size="small" icon={<PlusOutlined />} onClick={() => setNb({ node_id: node.id })}>{t('Add')}</Button>}>
                <Table size="small" rowKey="id" dataSource={node.neighbors} pagination={false}
                    columns={[
                        { title: t('Neighbor'), dataIndex: 'address', render: (a, r) => <Space size={4}><Text code>{a}</Text>{!r.lab && <Tag color="red">{t('External')}</Tag>}</Space> },
                        { title: 'ASN', dataIndex: 'remote_asn' },
                        { title: t('Local address'), dataIndex: 'local_address', render: (v) => v || '-' },
                        { title: t('Address families'), dataIndex: 'families', render: (f) => (f || []).join(', ') },
                        { title: 'MD5', dataIndex: 'has_password', render: (v) => (v ? t('Set') : '-') },
                        { title: t('Enable'), dataIndex: 'enabled', render: (v) => <Badge status={v ? 'success' : 'default'} /> },
                        {
                            title: '', width: 80, render: (_, r) => (
                                <Space size={4}>
                                    <Button size="small" icon={<EditOutlined />} onClick={() => setNb(r)} />
                                    <Popconfirm title={t('Delete this neighbor?')} onConfirm={() => del(`/neighbor/${r.id}/delete`)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                                </Space>
                            ),
                        },
                    ]} />
            </Card>
            <Card size="small" title={t('Announced prefixes')} extra={<Button size="small" icon={<PlusOutlined />} onClick={() => setPx({ node_id: node.id })}>{t('Add')}</Button>}>
                <Table size="small" rowKey={(r) => r.id || `${r.source}:${r.prefix}`} dataSource={node.prefixes} pagination={false}
                    columns={[
                        { title: t('Prefix'), dataIndex: 'prefix', render: (p) => <Text code>{p}</Text> },
                        { title: t('Source'), dataIndex: 'source', render: (s) => <Tag color={SRC_COLOR[s]}>{s}</Tag> },
                        { title: t('Desired state'), dataIndex: 'desired_state', render: (s) => (s === 'announce' ? t('Announce') : t('Withdraw')) },
                        {
                            title: t('Actual'), render: (_, r) => {
                                const on = (node.status?.announced || []).some((a) => a.prefix === r.prefix);
                                return <Badge status={on ? 'success' : 'default'} text={on ? (node.status?.mode === 'live' ? t('Announcing') : t('Dry run')) : '-'} />;
                            },
                        },
                        { title: 'Communities', dataIndex: 'communities', render: (v) => v || '-' },
                        {
                            title: '', width: 80, render: (_, r) => (r.id ? (
                                <Space size={4}>
                                    <Button size="small" icon={<EditOutlined />} onClick={() => setPx(r)} />
                                    <Popconfirm title={t('Delete this prefix?')} onConfirm={() => del(`/prefix/${r.id}/delete`)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                                </Space>
                            ) : <Tooltip title={t('Generated automatically')}><Text type="secondary">auto</Text></Tooltip>),
                        },
                    ]} />
            </Card>
            <NeighborModal value={nb} externalAllowed={externalAllowed} onClose={() => setNb(null)} onSaved={() => { setNb(null); reload(); }} />
            <PrefixModal value={px} onClose={() => setPx(null)} onSaved={() => { setPx(null); reload(); }} />
        </Space>
    );
};

const OwnedModal = ({ value, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const kind = Form.useWatch('kind', form);
    useEffect(() => {
        if (value) {
            form.setFieldsValue({
                prefix: '', kind: 'platform', user_id: null, origin_asn: null, roa_status: 'unknown', irr_status: 'unknown',
                loa_status: 'none', loa_ref: '', note: '', ...value, eip_float: !!Number(value.eip_float),
            });
        }
    }, [value, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const res = value.id ? await call('post', `/owned/${value.id}`, v) : await call('post', '/owned', v);
        setSaving(false);
        if (res.status === 'success') { message.success(t('Saved')); onSaved(); } else message.error(res.msg || t('Failed to save'));
    };
    const st = (opts) => opts.map((o) => ({ value: o, label: o }));
    return (
        <Modal open={!!value} title={value?.id ? t('Edit owned prefix') : t('Add owned prefix')} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose>
            <Form form={form} layout="vertical">
                <Space wrap>
                    <Form.Item name="prefix" label={t('Prefix')} rules={[{ required: true }]}><Input style={{ width: 220 }} disabled={!!value?.id} /></Form.Item>
                    <Form.Item name="kind" label={t('Type')} tooltip={t('Test records: documentation ranges only, sent to internal neighbors only')}>
                        <Select style={{ width: 140 }} disabled={!!value?.id} options={[{ value: 'platform', label: t('Platform-owned') }, { value: 'byoip', label: 'BYOIP' }, { value: 'test', label: t('Test') }]} />
                    </Form.Item>
                </Space>
                {kind === 'byoip' && (
                    <Space wrap>
                        <Form.Item name="user_id" label={t('User ID')} rules={[{ required: true }]}><InputNumber min={1} /></Form.Item>
                        <Form.Item name="origin_asn" label={t('Origin ASN')}><InputNumber min={1} max={4294967294} style={{ width: 160 }} /></Form.Item>
                    </Space>
                )}
                {kind === 'byoip' && (
                    <Space wrap>
                        <Form.Item name="roa_status" label="RPKI ROA"><Select style={{ width: 120 }} options={st(['unknown', 'valid', 'invalid', 'not_found'])} /></Form.Item>
                        <Form.Item name="irr_status" label="IRR"><Select style={{ width: 120 }} options={st(['unknown', 'valid', 'invalid', 'not_found'])} /></Form.Item>
                        <Form.Item name="loa_status" label="LOA"><Select style={{ width: 120 }} options={st(['none', 'submitted', 'verified', 'rejected'])} /></Form.Item>
                    </Space>
                )}
                {kind !== 'byoip' && <Form.Item name="eip_float" label={t('Floating EIPs')} valuePropName="checked" tooltip={t('EIPs in range are announced as /32 from their node')}><Switch /></Form.Item>}
                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <>
                            {kind === 'byoip' && <Form.Item name="loa_ref" label={t('LOA reference')}><Input /></Form.Item>}
                            {kind !== 'byoip' && <Form.Item name="origin_asn" label={t('Origin ASN')}><InputNumber min={1} max={4294967294} style={{ width: 160 }} /></Form.Item>}
                            <Form.Item name="note" label={t('Notes')}><Input /></Form.Item>
                        </>
                    ),
                }]} />
            </Form>
        </Modal>
    );
};

const OwnedTab = ({ rows, reload }) => {
    const { t } = useTranslation();
    const [edit, setEdit] = useState(null);
    const [filter, setFilter] = useState('all');
    const review = async (id, action) => {
        const res = await call('post', `/owned/${id}/review`, { action });
        res.status === 'success' ? message.success(t('Updated')) : message.error(res.msg || t('Failed'));
        reload();
    };
    const del = async (id) => {
        const res = await call('post', `/owned/${id}/delete`);
        res.status === 'success' ? message.success(t('Deleted')) : message.error(res.msg || t('Failed'));
        reload();
    };
    const pending = rows.filter((r) => r.approval === 'pending').length;
    const shown = filter === 'pending' ? rows.filter((r) => r.approval === 'pending') : rows;
    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Segmented value={filter} onChange={setFilter} options={[{ value: 'all', label: t('All') }, { value: 'pending', label: `${t('Pending')} (${pending})` }]} />
                <Button icon={<PlusOutlined />} onClick={() => setEdit({})}>{t('Add')}</Button>
            </Space>
            <Table size="small" rowKey="id" dataSource={shown} pagination={false} scroll={{ x: 'max-content' }}
                columns={[
                    { title: t('Prefix'), dataIndex: 'prefix', render: (p) => <Text code>{p}</Text> },
                    { title: t('Type'), dataIndex: 'kind', render: (k) => <Tag color={k === 'byoip' ? 'magenta' : k === 'test' ? 'blue' : 'default'}>{k}</Tag> },
                    { title: t('User'), dataIndex: 'user_id', render: (v) => v || '-' },
                    { title: 'ASN', dataIndex: 'origin_asn', render: (v) => v || '-' },
                    { title: 'ROA / IRR / LOA', render: (_, r) => (r.kind === 'byoip' ? `${r.roa_status} / ${r.irr_status} / ${r.loa_status}` : '-') },
                    { title: t('Float'), dataIndex: 'eip_float', render: (v) => (Number(v) ? <Tag color="purple">EIP</Tag> : '-') },
                    { title: t('Approval'), dataIndex: 'approval', render: (a, r) => <Tooltip title={(r.blockers || []).join('; ')}><Tag color={APPROVAL_COLOR[a]}>{a}</Tag></Tooltip> },
                    {
                        title: '', render: (_, r) => (
                            <Space size={4}>
                                {r.approval !== 'approved' && (
                                    <Popconfirm title={t('Once approved it can be announced')} onConfirm={() => review(r.id, 'approve')} disabled={(r.blockers || []).length > 0}>
                                        <Button size="small" type="primary" disabled={(r.blockers || []).length > 0}>{t('Approve')}</Button>
                                    </Popconfirm>
                                )}
                                {r.approval === 'pending' && <Button size="small" onClick={() => review(r.id, 'reject')}>{t('Deny')}</Button>}
                                {r.approval === 'approved' && (
                                    <Popconfirm title={t('Related announcements are withdrawn on the next sync')} onConfirm={() => review(r.id, 'revoke')}>
                                        <Button size="small" danger>{t('Revoke')}</Button>
                                    </Popconfirm>
                                )}
                                <Button size="small" icon={<EditOutlined />} onClick={() => setEdit(r)} />
                                <Popconfirm title={t('Delete this record?')} onConfirm={() => del(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                            </Space>
                        ),
                    },
                ]} />
            <OwnedModal value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
        </>
    );
};

const BgpAdmin = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editNode, setEditNode] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        const res = await call('get', '');
        setLoading(false);
        if (res.status === 'success') setData(res.data); else message.error(res.msg || t('Failed to load'));
    }, [t]);
    useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);
    const toggleKill = async (on) => {
        const res = await call('post', '/kill', { on: on ? 1 : 0 });
        res.status === 'success' ? message.success(res.msg) : message.error(res.msg || t('Failed'));
        load();
    };
    const nodes = useMemo(() => data?.nodes || [], [data]);
    const columns = [
        { title: t('Node'), dataIndex: 'name', render: (n, r) => <Space size={4}><span>{n}</span><Text type="secondary">#{r.id}</Text></Space> },
        { title: t('Mode'), render: (_, r) => <Tag color={MODE_COLOR[r.config?.mode || 'off']}>{r.config?.mode || 'off'}</Tag> },
        {
            title: t('Running'), render: (_, r) => {
                const s = r.status;
                if (!s) return <Text type="secondary">-</Text>;
                if (s.kill) return <Tag color="red">{t('Node kill switch')}</Tag>;
                return <Badge status={s.running ? 'processing' : 'default'} text={s.running ? dur(s.uptime) : t('Stop')} />;
            },
        },
        { title: 'ASN', render: (_, r) => r.status?.local_asn || r.config?.local_asn || '-' },
        { title: 'Router ID', render: (_, r) => r.status?.router_id || r.config?.router_id || '-' },
        {
            title: t('Sessions'), render: (_, r) => {
                const ns = r.status?.neighbors || [];
                if (!ns.length) return '-';
                const up = ns.filter((n) => n.state === 'established').length;
                return <Tag color={up === ns.length ? 'green' : up ? 'orange' : 'red'}>{up}/{ns.length}</Tag>;
            },
        },
        { title: t('Announce'), render: (_, r) => <Popover title={t('Announced prefixes')} content={prefixList((r.status?.announced || []).map((a) => a.prefix))}><Button type="link" size="small" style={{ padding: 0 }}>{(r.status?.announced || []).length}</Button></Popover> },
        { title: t('Received'), render: (_, r) => (r.status?.neighbors || []).reduce((s, n) => s + (n.received || 0), 0) },
        { title: t('Reported'), dataIndex: 'reported_at', render: (v) => v || '-' },
        { title: '', width: 50, render: (_, r) => <Button size="small" icon={<SettingOutlined />} onClick={() => setEditNode(r)} /> },
    ];
    return (
        <div>
            <PageHeader title={t('BGP announcements')} extra={(
                <Space>
                    {data?.available && (
                        <Popconfirm title={data.killed ? t('Lift the global kill switch?') : t('Global kill switch: stop BGP on all nodes now')} onConfirm={() => toggleKill(!data.killed)}>
                            <Button danger={!data.killed} type={data.killed ? 'primary' : 'default'} disabled={data.kill_env}>
                                {data.killed ? t('Lift kill switch') : t('Global kill switch')}
                            </Button>
                        </Popconfirm>
                    )}
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                </Space>
            )} />
            {data && !data.available && <Alert type="warning" showIcon message={t('BGP not enabled: run the database migration first')} />}
            {data?.killed && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={t('Global kill switch on: BGP stopped on all nodes')} />}
            {data?.available && (
                <Tabs items={[
                    {
                        key: 'nodes', label: t('Node'), children: (
                            <Table size="small" rowKey="id" loading={loading && !data} dataSource={nodes} columns={columns} pagination={false} scroll={{ x: 'max-content' }}
                                expandable={{ expandedRowRender: (r) => <NodeDetail node={r} reload={load} externalAllowed={data.external_peers_allowed} /> }} />
                        ),
                    },
                    {
                        key: 'owned', label: `${t('Owned prefixes / BYOIP')}${(data.owned || []).some((o) => o.approval === 'pending') ? ' •' : ''}`,
                        children: <OwnedTab rows={data.owned || []} reload={load} />,
                    },
                ]} />
            )}
            <NodeModal node={editNode} onClose={() => setEditNode(null)} onSaved={() => { setEditNode(null); load(); }} />
        </div>
    );
};

export default BgpAdmin;
